/**
 * The driver: their details, vehicle and documents (kept by the office), this phone's
 * notifications and offer sound, help and signing out.
 */
import { errorMessage, post } from "@taxi/mobile-web/api";
import { phone } from "@taxi/mobile-web/format";
import { BellIcon, LogOutIcon, PhoneIcon, ShieldIcon } from "@taxi/mobile-web/icons";
import { InstallHint } from "@taxi/mobile-web/install";
import { isStandalone, usePush } from "@taxi/mobile-web/push";
import { Avatar, Button, Card, Plate, Row, Sheet, TopBar, useToast } from "@taxi/mobile-web/ui";
import { useState } from "react";
import { shortDate } from "../components/parts";
import { testSound } from "../lib/device";
import { useDriver } from "../lib/driver";
import { useSession } from "../lib/session";
import type { DriverDocument } from "../lib/types";

function documentNote(doc: DriverDocument): string {
  switch (doc.status) {
    case "valid":
      return doc.expiresOn ? `Valid until ${shortDate(doc.expiresOn)}` : "With the office";
    case "expiring":
      return `Expires ${doc.daysLeft === 0 ? "today" : `in ${doc.daysLeft} day${doc.daysLeft === 1 ? "" : "s"}`}${doc.expiresOn ? ` (${shortDate(doc.expiresOn)})` : ""}`;
    case "expired":
      return doc.expiresOn ? `Expired on ${shortDate(doc.expiresOn)}` : "Expired";
    case "missing":
      return "The office doesn't have it yet";
  }
}

const DOCUMENT_STATUS: Record<DriverDocument["status"], string> = {
  valid: "OK",
  expiring: "Soon",
  expired: "Expired",
  missing: "Missing",
};

export function AccountScreen() {
  const { me, refresh } = useDriver();
  const { firm, firmPhone, signOut } = useSession();
  const toast = useToast();
  const push = usePush("driver", firm.webPush.publicKey);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const profile = me.profile;
  const vehicle = profile.vehicle;
  const status = me.live.status;

  async function togglePush() {
    try {
      if ((await push.toggle()) === "blocked")
        toast("Notifications are blocked for this site in your browser settings.");
    } catch (error) {
      toast(errorMessage(error), "error");
    }
  }

  async function leave() {
    setSigningOut(true);
    try {
      // Not on shift with nobody here to take the jobs.
      if (status === "available" || status === "paused") await post("/driver/shift/clock-off");
      await signOut();
    } catch (error) {
      toast(errorMessage(error), "error");
      setSigningOut(false);
      void refresh();
    }
  }

  const pushSubtitle = !push.available
    ? "Not available in this browser"
    : push.support === "add-to-home-screen"
      ? "Add this app to your Home Screen first"
      : push.state === "on"
        ? "On: offers reach you even when you've switched apps"
        : push.state === "blocked"
          ? "Blocked in your browser settings"
          : "Off";

  return (
    <div className="account">
      <TopBar title="Account" />
      <div className="screen-body">
        <Card className="profile-card">
          <Avatar name={profile.name} photoUrl={profile.photoUrl} size={56} />
          <div>
            <strong>{profile.name}</strong>
            <span>{phone(profile.phone)}</span>
            {profile.badgeNumber && <span>Badge {profile.badgeNumber}</span>}
          </div>
        </Card>

        {vehicle && (
          <Card className="vehicle">
            <Plate registration={vehicle.registration} />
            <div>
              <strong>
                {vehicle.colour} {vehicle.make} {vehicle.model}
              </strong>
              <span>
                {vehicle.vehicleTypeName} · {vehicle.seats} seats
                {vehicle.plateNumber && ` · Council plate ${vehicle.plateNumber}`}
              </span>
            </div>
          </Card>
        )}

        <h2 className="section-title">Documents</h2>
        <div className="row-group">
          {me.documents.map((doc) => (
            <Row
              key={doc.kind}
              icon={<ShieldIcon />}
              title={doc.label}
              subtitle={documentNote(doc)}
              right={
                <span className={`doc-status doc-${doc.status}`}>
                  {DOCUMENT_STATUS[doc.status]}
                </span>
              }
            />
          ))}
        </div>
        <p className="fine-print">
          The office keeps your documents. When you renew one, send them the new one.
        </p>

        <h2 className="section-title">This phone</h2>
        <div className="row-group">
          <Row
            icon={<BellIcon />}
            title="Notifications"
            subtitle={pushSubtitle}
            right={
              push.available && push.support === "supported" ? (
                <button
                  type="button"
                  role="switch"
                  aria-checked={push.state === "on"}
                  aria-label="Notifications"
                  className="switch"
                  disabled={push.state === "busy" || push.state === "blocked"}
                  onClick={togglePush}
                />
              ) : undefined
            }
          />
          <Row
            icon={<BellIcon />}
            title="Offer sound"
            subtitle="Check you can hear it over the road"
            right={
              <Button block={false} variant="secondary" onClick={testSound}>
                Play
              </Button>
            }
          />
        </div>
        {!isStandalone() && (
          <InstallHint
            title="Keep the app on your Home Screen"
            why="It opens in one tap, like an app, and can get job offers as notifications."
          />
        )}

        <h2 className="section-title">Help</h2>
        <div className="row-group">
          <Row
            icon={<PhoneIcon />}
            title="Call the office"
            subtitle={phone(firmPhone)}
            onClick={() => {
              window.location.href = `tel:${firmPhone}`;
            }}
          />
          <Row
            icon={<LogOutIcon />}
            title="Sign out"
            danger
            onClick={() => setConfirmSignOut(true)}
          />
        </div>
      </div>

      <Sheet open={confirmSignOut} title="Sign out?" onClose={() => setConfirmSignOut(false)}>
        {status === "on_job" ? (
          <p>Finish your job first: you're still on it.</p>
        ) : (
          <>
            <p>
              {status === "off"
                ? "You can sign back in with a new code."
                : "You'll be clocked off too, so no jobs come to you."}
            </p>
            <Button variant="danger" loading={signingOut} onClick={leave}>
              Sign out
            </Button>
          </>
        )}
        <Button variant="ghost" onClick={() => setConfirmSignOut(false)}>
          Cancel
        </Button>
      </Sheet>
    </div>
  );
}
